import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Download, Film } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { pauseOthers } from "@/components/StudioBand";

const src = (key, kind) => `${process.env.REACT_APP_BACKEND_URL}/api/admin/cutdowns/${key}/${kind}`;

const Cut = ({ item }) => (
  <section className="flex min-w-0 flex-col gap-3 rounded-3xl border border-border bg-card p-5" data-testid={`cutdown-${item.key}`}>
    <div>
      <p className="font-mono text-[11px] uppercase tracking-widest text-primary">1 minute · 9:16 captions · {item.length}s</p>
      <h2 className="mt-1 font-serif text-xl">{item.group === "showreel" ? "Showreel" : `${item.label} · ${item.title}`}</h2>
    </div>
    <video controls playsInline preload="metadata" onPlay={pauseOthers} poster={src(item.key, "poster")} className="aspect-[9/16] w-full max-w-[300px] rounded-2xl bg-black" data-testid={`cutdown-video-${item.key}`}>
      <source src={src(item.key, "cc")} type="video/mp4" />
    </video>
    <div className="flex flex-wrap gap-2">
      <a href={`${src(item.key, "cc")}?download=1`} data-testid={`cutdown-download-${item.key}`}
        className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground"><Download size={14} /> Download</a>
      <button type="button" data-testid={`cutdown-copy-caption-${item.key}`} onClick={() => navigator.clipboard.writeText(item.caption).then(() => toast.success("Post caption, link + hashtags copied"))}
        className="inline-flex items-center gap-1.5 rounded-full border border-primary px-4 py-1.5 text-sm font-semibold text-primary"><Copy size={14} /> Copy caption</button>
    </div>
    <details className="text-sm text-muted-foreground" data-testid={`cutdown-script-${item.key}`}>
      <summary className="cursor-pointer font-semibold text-foreground">Voiceover script</summary>
      <p className="mt-2 leading-relaxed">{item.script}</p>
    </details>
    <details className="text-sm text-muted-foreground" data-testid={`cutdown-caption-${item.key}`}>
      <summary className="cursor-pointer font-semibold text-foreground">Post caption</summary>
      <p className="mt-2 leading-relaxed">{item.caption}</p>
    </details>
  </section>
);

const ORDER = (k) => (k.startsWith("showreel") ? 0 : k.startsWith("classroom") ? 1 : 2);

export default function AdminCutdowns() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (user?.role !== "admin") return;
    api.get("/admin/cutdowns").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, [user]);
  if (user?.role !== "admin") return <p className="p-10" data-testid="cutdowns-denied">Admins only. <Link to="/" className="text-primary underline" data-testid="cutdowns-home-link">Go to the homepage</Link></p>;
  const sorted = [...(items || [])].sort((a, b) => ORDER(a.key) - ORDER(b.key));
  return (
    <main className="mx-auto max-w-6xl space-y-8 px-6 py-10" data-testid="admin-cutdowns-page">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="cutdowns-back"><ArrowLeft size={14} /> Admin</Link>
      <div>
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Film size={14} /> Admin only</p>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl">Social video cutdowns</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">One-minute, phone-shaped videos with captions, for Instagram Reels, TikTok, Stories and WhatsApp status. No prices: each one ends on thejthub.com, and the copied post caption includes the link. Not shown on the public site.</p>
      </div>
      {items === null && <p data-testid="cutdowns-loading">Loading…</p>}
      {items?.length === 0 && <p data-testid="cutdowns-empty">No cutdowns yet.</p>}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{sorted.map((i) => <Cut key={i.key} item={i} />)}</div>
    </main>
  );
}
